import ac from 'argument-contracts';
import { coerceArray } from '@meltwater/coerce';
import { DateRange } from '@meltwater/date-range/cjs';
import { Filter } from '@meltwater/filters';
import { getDateRangeLimitForInputs } from '../date-range-limit';
import InputId from '@meltwater/inputs';
import moment from 'moment-timezone';
import UTC_TZ_REGEX from '../utc-tz-regex';

const MAX_INPUTS = 30;

function isDateDifferenceSafe(startDate, endDate, limit) {
    const diff = moment(startDate).diff(moment(endDate), 'days');
    if(Math.abs(diff) > limit) {
        return false;
    }
    return true;
}

/**
 * Constant value to use the date range limit for the provided inputs as the
 * value for the `dateRangeMaxDays` option when constructing
 * AuthorizedAppletHostData.
 */
export const DATE_RANGE_LIMIT_FOR_INPUTS = Symbol('Determine dateRangeMaxDays from inputs');

/**
 * A reconstitution of the data of a single namespace of an AppletHost.
 * This is intended to act as the deserializer of an AppletHostSerialization
 * sent over a network communication with an Authorization property.
 *
 * @deprecated until v4
 *
 * @param {object} data - See below
 * @param {string} [data.Authorization] - The JWT for the user interacting with the AppletHost (in order to personalize data)
 * @param {string} [data.companyId] - The active companyId of the user interacting with the AppletHost (in order to personalize data)
 * @param {string} [data.endDate] - The date range end. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE. < 366 days after startDate
 * @param {Filter[]} data.filters - The filters as an array of Filter objects which can each be coerced to a @meltwater/filters Filter
 * @param {string} data.inputs - The inputs as comma-separated string of values that can be parsed as a @meltwater/inputs InputId. Length <= 30.
 * @param {string} data.noNewDocsAfter - The time after which any newly indexed results should be excluded. This is set of the coordinatedQueryTime parameter.
 * @param {DateRangeTypes} data.rangeType - The date range type. Must be valid @meltwater/date-range DateRangeTypes value
 * @param {string} [data.startDate] - The date range start. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE.
 * @param {string} data.timezone - A valid moment timezone
 * @param {object} [options] - See below
 * @param {number} [options.dateRangeMaxDays=366] - The maximum number of days to allow when constructing dateRange
 * @throws {TypeError} `data` is not provided as an object
 * @throws {Error} `data.inputs` exceeds maxiumum length
 * @throws {TypeError} `data.timezone` is not a valid moment timezone
 * @throws {TypeError} `data.endDate` is malformatted
 * @throws {Error} Constructed date range exceeds `options.dateRangeMaxDays`
 * @throws {TypeError} `data.noNewDocsAfter` is malformatted
 */
/**
 * A reconstitution of a the data of a single namespace of an AppletHost.
 * This is intended to act as the deserializer of an AppletHostSerialization
 * sent over a network communication with an Authorization property.
 *
 * @param {object} data - See below
 * @param {string} [data.Authorization] - The JWT for the user interacting with the AppletHost (in order to personalize data)
 * @param {string} [data.companyId] - The active companyId of the user interacting with the AppletHost (in order to personalize data)
 * @param {string} [data.endDate] - The date range end. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE. < 366 days after startDate
 * @param {Filter[]} data.filters - The filters as an array of Filter objects which can each be coerced to a @meltwater/filters Filter
 * @param {string[]} data.inputs - The inputs as an array of String which can be parsed as a @meltwater/inputs InputId. Length <= 30
 * @param {string} data.noNewDocsAfter - The time after which any newly indexed results should be excluded. This is set of the coordinatedQueryTime parameter.
 * @param {DateRangeTypes} data.rangeType - The date range type. Must be valid @meltwater/date-range DateRangeTypes value
 * @param {string} [data.startDate] - The date range start. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE.
 * @param {string} data.timezone - A valid moment timezone
 * @param {object} [options] - See below
 * @param {number} [options.dateRangeMaxDays=366] - The maximum number of days to allow when constructing dateRange
 * @throws {TypeError} `data` is not provided as an object
 * @throws {Error} `data.inputs` exceeds maxiumum length
 * @throws {TypeError} `data.timezone` is not a valid moment timezone
 * @throws {TypeError} `data.endDate` is malformatted
 * @throws {Error} Constructed date range exceeds `options.dateRangeMaxDays`
 * @throws {TypeError} `data.noNewDocsAfter` is malformatted
 *
 * @prop {string} Authorization - The JWT for the user interacting with the AppletHost (in order to personalize data)
 * @prop {string} companyId - The active companyId of the user interacting with the AppletHost (in order to personalize data)
 * @prop {DataRange} dateRange - The date range as a @meltwater/date-range DateRange
 * @prop {InputId[]} inputs - The array of inputs as @meltwater/inputs InputId
 * @prop {string} noNewDocsAfter - The time after which any newly indexed results should be excluded. Formatted as yyyy-mm-ddTmm:ss±hh:mm.
 * @prop {string} timezone - A valid moment timezone
 */
export class AuthorizedAppletHostData{
    constructor(data, { dateRangeMaxDays = DATE_RANGE_LIMIT_FOR_INPUTS } = { }) {
        if (!data || typeof data !== 'object') {
            throw new TypeError(`data must be provided as an object. Provided: ${JSON.stringify(data)}`);
        }

        ac.assertString(data.Authorization, 'Authorization');
        ac.assertString(data.companyId, 'companyId');

        const filters = data.filters !== undefined
            ? coerceArray(data.filters, Filter, `filters must be an array of objects that can be coerced to Filter. Provided value: ${JSON.stringify(data.filters)}`)
            : [];

        let validInputs = [];
        if (Array.isArray(data.inputs)) {
            ac.assertArrayOf(data.inputs, String, 'inputs');
            validInputs = data.inputs.map(InputId.parse);
        }
        else {
            ac.assertString(data.inputs, 'inputs');
            if (data.inputs.length > 0) {
                validInputs = data.inputs.split(',').map(InputId.parse);
            }
        }

        if(validInputs.length > MAX_INPUTS) {
            throw new Error(`inputs contains too many items! A maximum of ${MAX_INPUTS} inputs is allowed. Provided value: ${data.inputs}`);
        }

        if(!data.timezone || typeof data.timezone !== 'string' || !moment.tz.zone(data.timezone)) {
            throw new TypeError(`timezone must be a valid moment timezone. Provided value: ${data.timezone}`);
        }

        const dateRange = new DateRange(data);

        const dateRangeLimit = dateRangeMaxDays === DATE_RANGE_LIMIT_FOR_INPUTS
            ? getDateRangeLimitForInputs(validInputs)
            : dateRangeMaxDays;

        if(!isDateDifferenceSafe(dateRange.startDate, dateRange.getEndDate(), dateRangeLimit)) {
            throw new Error(`startDate and endDate must be within ${dateRangeLimit} days of each other. Provided values: ${data.startDate} - ${data.endDate}`);
        }

        if(!UTC_TZ_REGEX.test(data.noNewDocsAfter)) {
            throw new TypeError(`noNewDocsAfter must in the format UTC+Timezone. Eg 2010-06-14T02:31:00-04:00. Provided value: ${data.noNewDocsAfter}`);
        }

        this.authorization = data.Authorization;
        this.companyId = data.companyId;
        this.dateRange = dateRange;
        this.filters = filters;
        this.inputs = validInputs;
        this.noNewDocsAfter = data.noNewDocsAfter;
        this.timezone = data.timezone;

        Object.freeze(this);
    }
}
